import { IFilterOption } from '../../indigofair/data/IFilterOption';
import { IFilterOptionGroup } from '../../indigofair/data/IFilterOptionGroup';
import { IRangeFilter } from '../../indigofair/data/IRangeFilter';
import { IUserMilestone } from '../../indigofair/data/IUserMilestone';

export namespace IFilterSection {
  /**
   * This defines the type of selection for this FilterSection.
   * SINGLE means that 0 or 1 options from the `options` list can be selected.
   * MULTIPLE means that any number of options from the `options` list can be selected.
   * SINGLE_REQUIRED means that exactly 1 option from the `options` list must be selected. The backend must supply a default value in case the frontend doesn't include an option in its request.
   * TOGGLE means that the filter section will have only one option which will be working like a toggle if the option is selected
   * MULTIPLE_OPTIONAL_SELECTIONS means that there will be multiple toggles and the user can select any number of them.
   * NAVIGATION means that the filter section should appear as a tile and disappear when an option is selected. It should behave like a TOGGLE (i.e. only one selection can be made).
   */
  export enum SelectionType {
    SELECTION_TYPE_UNKNOWN = 'SELECTION_TYPE_UNKNOWN',
    SINGLE = 'SINGLE',
    MULTIPLE = 'MULTIPLE',
    SINGLE_REQUIRED = 'SINGLE_REQUIRED',
    MULTIPLE_SELECTION_OR_SINGLE_RANGE = 'MULTIPLE_SELECTION_OR_SINGLE_RANGE',
    TOGGLE = 'TOGGLE',
    MULTIPLE_OPTIONAL_SELECTIONS = 'MULTIPLE_OPTIONAL_SELECTIONS',
    NAVIGATION = 'NAVIGATION',
  }

  /**
   * This is a useful identifier for all FilterSections, presented in the enum format.
   * Should be used as an id, mapper or determinant for specific behaviour for the frontends.
   * Should not be used as an exhaustive list on the code because more fields may be added in future versions of the
   * backend.
   * Not all FieldNames are returned on all apis, and some field names might be used to identify multiple sections.
   */
  export enum FieldName {
    FIELD_NAME_UNKNOWN = 'FIELD_NAME_UNKNOWN',
    CATEGORY = 'CATEGORY',
    WHOLESALE_PRICE = 'WHOLESALE_PRICE',
    MAKER_MINIMUM = 'MAKER_MINIMUM',
    MAKER_VALUE = 'MAKER_VALUE',
    BRAND = 'BRAND',
    INSIDER = 'INSIDER',
    LOCATION = 'LOCATION',
    MAKER_SPECIAL = 'MAKER_SPECIAL',
    LEAD_TIME = 'LEAD_TIME',
    SHIPS_FROM = 'SHIPS_FROM',
    SORTING = 'SORTING',
    CLEARANCE = 'CLEARANCE',
    COLLECTION_PRICE = 'COLLECTION_PRICE',
    RETAIL_PRICE = 'RETAIL_PRICE',
    DELIVERY_METHOD = 'DELIVERY_METHOD',
    PRODUCT_ATTRIBUTE = 'PRODUCT_ATTRIBUTE',
    RETAILER_TYPE = 'RETAILER_TYPE',
    PROXIMITY = 'PROXIMITY',
    /**
     * used by ConsumerBestseller filters
     */
    FOR_PERIOD = 'FOR_PERIOD',
    /**
     * used by ConsumerBestseller filters
     */
    BREAKDOWN = 'BREAKDOWN',
    /**
     * used by ConsumerBestseller filters
     */
    BESTSELLER_CATEGORY = 'BESTSELLER_CATEGORY',
    /**
     * used by ConsumerBestseller filters
     */
    STORE_TYPE = 'STORE_TYPE',
    /**
     * used by ConsumerBestseller filters
     */
    BESTSELLER_MAKER_VALUE = 'BESTSELLER_MAKER_VALUE',
    /**
     * used by ConsumerBestseller filters
     */
    BESTSELLER_REGION = 'BESTSELLER_REGION',
    /**
     * Flash sale discount filter
     */
    DISCOUNT_PERCENTAGE = 'DISCOUNT_PERCENTAGE',
    /**
     * Immediate vs. preorder filter
     */
    DELIVERY_WINDOW = 'DELIVERY_WINDOW',
    /**
     * hack week 2020 - Eco filters remove after 2021-01-01
     * @deprecated
     */
    BRAND_CERTIFICATIONS = 'BRAND_CERTIFICATIONS',
    /**
     * Used by preorder marketplace
     */
    PREORDER_WINDOW = 'PREORDER_WINDOW',
    /**
     * Used by 2021 Faire Fashion Week
     * @deprecated
     */
    FAIRE_FASHION_WEEK = 'FAIRE_FASHION_WEEK',
    TOP_SHOP = 'TOP_SHOP',
    MID_PRICE_POINT_PLUS = 'MID_PRICE_POINT_PLUS',
    ON_MARKET_LIST = 'ON_MARKET_LIST',
    /**
     * only used for FFW
     * @deprecated
     */
    ADDED_TO_CART = 'ADDED_TO_CART',
    PREVIOUSLY_ORDERED = 'PREVIOUSLY_ORDERED',
    /**
     * only used for FFW
     * @deprecated
     */
    FFW_NEVER_ORDERED_DISCOUNT = 'FFW_NEVER_ORDERED_DISCOUNT',
    SHOP_BASED_IN_COUNTRY = 'SHOP_BASED_IN_COUNTRY',
    BRAND_NEW_PRODUCTS = 'BRAND_NEW_PRODUCTS',
    BASED_IN = 'BASED_IN',
    MADE_IN = 'MADE_IN',
    BRAND_PROMOTION = 'BRAND_PROMOTION',
    MARKET_LIST = 'MARKET_LIST',
    EUROPEAN_LOCATION = 'EUROPEAN_LOCATION',
    SHIP_WINDOW = 'SHIP_WINDOW',
    /**
     * only used for FFW
     * @deprecated
     */
    FFW_PREVIOUSLY_ORDERED = 'FFW_PREVIOUSLY_ORDERED',
    /**
     * @deprecated
     */
    FAIRE_SPECIALS = 'FAIRE_SPECIALS',
    USER = 'USER',
    NEW_PRODUCTS = 'NEW_PRODUCTS',
    TAXONOMY_TYPE = 'TAXONOMY_TYPE',
    CART_SUBMITTED_FOR_REVIEW = 'CART_SUBMITTED_FOR_REVIEW',
    FAIRE_PROTECTION = 'FAIRE_PROTECTION',
    BRAND_PROMOTION_TOGGLE = 'BRAND_PROMOTION_TOGGLE',
    LOW_MINIMUM = 'LOW_MINIMUM',
    /**
     * @deprecated
     */
    MARKUP = 'MARKUP',
    /**
     * Provides granular locations within countries
     */
    FAIRE_DETERMINED_LOCATION = 'FAIRE_DETERMINED_LOCATION',
    BRAND_SAMPLES = 'BRAND_SAMPLES',
    /**
     * Used by Orders page filters
     */
    ORDER_DATE = 'ORDER_DATE',
    /**
     * Used by orders page filters
     */
    ORDER_STATUS = 'ORDER_STATUS',
    /**
     * Used by Eco-friendly product attributes filter
     */
    ECO_FRIENDLY_PRODUCT_ATTRIBUTES = 'ECO_FRIENDLY_PRODUCT_ATTRIBUTES',
    /**
     * Used by Eco-friendly product attribute value quick filter
     */
    ECO_FRIENDLY_PRODUCT_ATTRIBUTE_VALUES = 'ECO_FRIENDLY_PRODUCT_ATTRIBUTE_VALUES',
    EXCLUSIVITY = 'EXCLUSIVITY',
    /**
     * only used for new brands/new arrivals free shipping
     */
    NEW_ARRIVALS_FREE_SHIPPING = 'NEW_ARRIVALS_FREE_SHIPPING',
    /**
     * @deprecated
     */
    TRENDING = 'TRENDING',
    /**
     * Used by Orders page filtering by store.
     */
    RETAILER_STORE = 'RETAILER_STORE',
    /**
     * Used to surface top leaf category pills as toggles.
     */
    TOP_CATEGORY_PILLS = 'TOP_CATEGORY_PILLS',
    /**
     * Used to surface leaf categories as toggles.
     */
    CATEGORIES_FOR_YOU = 'CATEGORIES_FOR_YOU',
    /**
     * Filters products that have at least one option with a GTIN
     */
    GLOBAL_TRADE_ITEM_NUMBER = 'GLOBAL_TRADE_ITEM_NUMBER',
  }

  /**
   * This defines the backend usage of this FilterSection.
   * Used on the web frontend to separate out sections for filtering from those used for sorting.
   * Should not be used as an exhaustive list on the code because more fields my be added in future versions of the
   * backend.
   */
  export enum FieldFunction {
    FIELD_FUNCTION_UNKNOWN = 'FIELD_FUNCTION_UNKNOWN',
    FILTER = 'FILTER',
    SORT = 'SORT',
  }

  export namespace IFilterBanner {
    /**
     * Return an instance of IFilterBanner with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IFilterBanner>): IFilterBanner =>
      ({
        ...(partial ?? {}),
      }) as IFilterBanner;
  }

  export interface IFilterBanner {
    message: string | undefined;
    text_color_hex: string | undefined;
    background_color_hex: string | undefined;
  }

  export namespace IFilterTooltip {
    /**
     * Return an instance of IFilterTooltip with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IFilterTooltip>): IFilterTooltip =>
      ({
        ...(partial ?? {}),
      }) as IFilterTooltip;
  }

  export interface IFilterTooltip {
    title: string | undefined;
    message: string | undefined;
    cta_text: string | undefined;
    dismiss_milestone: IUserMilestone | undefined;
    seen_milestone: IUserMilestone | undefined;
  }

  export enum BadgeType {
    BADGE_TYPE_UNKNOWN = 'BADGE_TYPE_UNKNOWN',
    NEW_BADGE = 'NEW_BADGE',
  }

  export namespace IFilterBadge {
    /**
     * Return an instance of IFilterBadge with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IFilterBadge>): IFilterBadge =>
      ({
        ...(partial ?? {}),
      }) as IFilterBadge;
  }

  export interface IFilterBadge {
    badge_type: keyof typeof IFilterSection.BadgeType | undefined;
    dismiss_milestone: IUserMilestone | undefined;
  }

  /**
   * Return an instance of IFilterSection with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IFilterSection>): IFilterSection =>
    ({
      options: [],
      range_filters: [],
      option_groups: [],
      ...(partial ?? {}),
    }) as IFilterSection;
}

/**
 * This represents a single filter block which can be comprised of a list of options.
 * Depending on the selectionType, the client can select one or multiple of those options.
 */
export interface IFilterSection {
  selection_type: keyof typeof IFilterSection.SelectionType | undefined;
  /**
   * This is a non-unique identifier for this FilterSection.
   * More FieldName's might be added by backend at any time.
   */
  field_name: keyof typeof IFilterSection.FieldName | undefined;
  /**
   * This is the actual display name for the field. Can be used as a unique identifier for FilterSections.
   * It's an English human-readable description of the Section that should be displayed as is by frontends.
   * This can be i18n'ed in the future in the backend but right now it's always in English.
   */
  display_name: string | undefined;
  /**
   * This is displayName but with formatting.
   * Formatting included something like `displayName (n)` where n is the number of options selected.
   */
  formatted_display_name: string | undefined;
  /**
   * The pillText for non-multiple SelectionType filter pills.
   * This field is only used in visual filter option projects.
   * The pillText changes to the filter option name if the user selects a non-multiple SelectionType filter,
   * which is duplicated with what we show on the visual filter options.
   * We should always show shortDisplayName as pillText in visual filter option projects for non-multiple
   * SelectionType filter sections.
   */
  short_display_name: string | undefined;
  /**
   * This option controls whether a suggestion/autocomplete/search box is to be displayed in this field.
   * Fields that have many options will probably include this.
   * This is a backend suggestion but UI implementation details should not affect the usage of the API.
   */
  searchable: boolean | undefined;
  /**
   * This is the list of options for this field, one (or many) of which the user will select.
   */
  options: Array<IFilterOption>;
  /**
   * The list of range filters that can be applied to this field.
   * Deprecated without replacement. Feature is being dropped
   * @deprecated
   */
  range_filters: Array<IRangeFilter>;
  field_function: keyof typeof IFilterSection.FieldFunction | undefined;
  default_option_key: string | undefined;
  /**
   * This is the text that should be shown in the a filter pill UI element.
   */
  pill_text: string | undefined;
  option_groups: Array<IFilterOptionGroup>;
  search_label_text: string | undefined;
  banner: IFilterSection.IFilterBanner | undefined;
  tooltip: IFilterSection.IFilterTooltip | undefined;
  badge: IFilterSection.IFilterBadge | undefined;
}
